import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import ErrorBoundary from "@/components/ErrorBoundary";

import DashboardLayout from "@/components/DashboardLayout";
import LoginPage from "@/pages/LoginPage";
import ForceChangePasswordPage from "@/pages/ForceChangePasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import WorkRequestsPage from "@/pages/WorkRequestsPage";
import WorkRequestDetailPage from "@/pages/WorkRequestDetailPage";
import WorkOrdersPage from "@/pages/WorkOrdersPage";
import WorkOrderDetailPage from "@/pages/WorkOrderDetailPage";
import BranchesPage from "@/pages/BranchesPage";
import RoomsAssetsPage from "@/pages/RoomsAssetsPage";
import UsersPage from "@/pages/UsersPage";
import CostAnalyticsPage from "@/pages/CostAnalyticsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import MoltBotHelpPage from "@/pages/MoltBotHelpPage";
import PMPlansPage from "@/pages/PMPlansPage";
import InventoryPage from "@/pages/InventoryPage";
import ImportCodesPage from "@/pages/ImportCodesPage";
import SearchResultsPage from "@/pages/SearchResultsPage";
import AssetDetailPage from "@/pages/AssetDetailPage";
import RoomDetailPage from "@/pages/RoomDetailPage";
import SLAPoliciesPage from "@/pages/SLAPoliciesPage";
import SLACompliancePage from "@/pages/SLACompliancePage";
import SystemStatusPage from "@/pages/SystemStatusPage";
import DataChecksPage from "@/pages/DataChecksPage";
import HelpPage from "@/pages/HelpPage";
import ChatPage from "@/pages/ChatPage";
import SuperAdminAuditPage from "@/pages/SuperAdminAuditPage";
import SuperAdminConsolePage from "@/pages/SuperAdminConsolePage";
import ReportsPage from "@/pages/ReportsPage";
import MyMaintenancePage from "@/pages/MyMaintenancePage";

function KeyboardShortcutsWrapper({ children }) {
  useKeyboardShortcuts();
  useDocumentTitle();
  return children;
}

function ProtectedRoute({ children, allowedRoles, superAdminOnly }) {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (superAdminOnly && user?.email?.toLowerCase() !== 'ahmad.disi@unknownroom.com') {
    return <Navigate to={user?.role === 'requester' ? '/my-maintenance' : '/dashboard'} replace />;
  }
  // super_admin has all admin privileges
  const effectiveRoles = allowedRoles && allowedRoles.includes('admin')
    ? [...allowedRoles, 'super_admin']
    : allowedRoles;
  if (effectiveRoles && !effectiveRoles.includes(user?.role)) {
    return <Navigate to={user?.role === 'requester' ? '/my-maintenance' : '/dashboard'} replace />;
  }
  return <DashboardLayout>{children}</DashboardLayout>;
}

function AppRoutes() {
  const { isAuthenticated, user } = useAuth();

  // Global gate: force password change blocks EVERYTHING
  if (isAuthenticated && user?.must_change_password) {
    return (
      <Routes>
        <Route path="/login" element={<Navigate to="/change-password" replace />} />
        <Route path="*" element={<ForceChangePasswordPage />} />
      </Routes>
    );
  }

  const homeRedirect = () => {
    if (!isAuthenticated) return '/login';
    if (user?.role === 'requester') return '/my-maintenance';
    return '/dashboard';
  };

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to={homeRedirect()} replace /> : <LoginPage />} />
      <Route path="/dashboard" element={<ProtectedRoute allowedRoles={['admin', 'supervisor', 'branch_manager', 'technician']}><DashboardPage /></ProtectedRoute>} />
      <Route path="/my-maintenance" element={<ProtectedRoute allowedRoles={['requester']}><MyMaintenancePage /></ProtectedRoute>} />
      <Route path="/work-requests" element={<ProtectedRoute><WorkRequestsPage /></ProtectedRoute>} />
      <Route path="/work-requests/:id" element={<ProtectedRoute><WorkRequestDetailPage /></ProtectedRoute>} />
      <Route path="/work-orders" element={<ProtectedRoute><WorkOrdersPage /></ProtectedRoute>} />
      <Route path="/work-orders/:id" element={<ProtectedRoute><WorkOrderDetailPage /></ProtectedRoute>} />
      <Route path="/branches" element={<ProtectedRoute allowedRoles={['admin']}><BranchesPage /></ProtectedRoute>} />
      <Route path="/rooms" element={<ProtectedRoute allowedRoles={['admin', 'supervisor', 'branch_manager']}><RoomsAssetsPage /></ProtectedRoute>} />
      <Route path="/rooms/:id" element={<ProtectedRoute allowedRoles={['admin', 'supervisor', 'branch_manager']}><RoomDetailPage /></ProtectedRoute>} />
      <Route path="/assets" element={<ProtectedRoute allowedRoles={['admin', 'supervisor', 'branch_manager']}><RoomsAssetsPage /></ProtectedRoute>} />
      <Route path="/assets/:id" element={<ProtectedRoute allowedRoles={['admin', 'supervisor', 'branch_manager']}><AssetDetailPage /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute allowedRoles={['admin', 'supervisor', 'branch_manager']}><UsersPage /></ProtectedRoute>} />
      <Route path="/cost-analytics" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><CostAnalyticsPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute allowedRoles={['admin', 'supervisor', 'branch_manager']}><NotificationsPage /></ProtectedRoute>} />
      <Route path="/pm-plans" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><PMPlansPage /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute allowedRoles={['admin', 'supervisor', 'branch_manager']}><InventoryPage /></ProtectedRoute>} />
      <Route path="/sla-policies" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><SLAPoliciesPage /></ProtectedRoute>} />
      <Route path="/sla-compliance" element={<ProtectedRoute allowedRoles={['admin', 'supervisor']}><SLACompliancePage /></ProtectedRoute>} />
      <Route path="/import-codes" element={<ProtectedRoute allowedRoles={['admin']}><ImportCodesPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute allowedRoles={['admin', 'supervisor', 'branch_manager']}><ReportsPage /></ProtectedRoute>} />
      <Route path="/system-status" element={<ProtectedRoute allowedRoles={['admin']}><SystemStatusPage /></ProtectedRoute>} />
      <Route path="/data-checks" element={<ProtectedRoute allowedRoles={['admin']}><DataChecksPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/super-admin-audit" element={<ProtectedRoute><SuperAdminAuditPage /></ProtectedRoute>} />
      <Route path="/super-admin" element={<ProtectedRoute><SuperAdminConsolePage /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><SearchResultsPage /></ProtectedRoute>} />
      <Route path="/moltbot" element={<ProtectedRoute superAdminOnly><MoltBotHelpPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to={homeRedirect()} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ErrorBoundary>
          <BrowserRouter>
            <KeyboardShortcutsWrapper>
              <AppRoutes />
            </KeyboardShortcutsWrapper>
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </ErrorBoundary>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
